import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@reactuses/core";
import { useQuery } from "@tanstack/react-query";
import { useBlockWindowQuery } from "../../../table/useBlockWindowQuery";
import { useServerTable } from "../../../table/tableFeatures";
import { usePersistedTableState } from "../../../table/usePersistedTableState";
import { useCategoriesQuery } from "../../../../hooks/useCategories";
import { MdRotateLeft } from "react-icons/md";
import { Table } from "../../../table/Table";
import { Button } from "../../basic/Button";
import { RT8FilterWidget } from "../../../../modules/merchandising/components/tables/FilterWidget";
import { RT8Pagination } from "../../../table/Pagination";
import { SearchInput } from "../../../fields/base/TextField";
import { RT8ColumnSelector } from "../MetricSelector";
import { idsToVisibilityState, resetTable, selectedIdsToSelectionState } from "../../../table/utils";
import { Flex, HStack, VStack } from "../../../../../styled-system/jsx";
import { INVENTORY_STATE_OPTIONS, SALE_STATE_OPTIONS } from "../../../custom/merchandise/constants";
import { ENROLL_OPTIONS } from "../../../../modules/advertising/filters/productLifecycleFilters";
import { css } from "../../../../../styled-system/css";
import { useGetV4ProductReportOptions } from "../../../../modules/perf_reports/api/product/v4ProductReportOptions";
import { useTagFilterOptions } from "../../../../modules/tags/api/useTagFilterOptions";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { toTitleCase } from "../../../../utils/formatText";
import { useGetV4ProductReportByProduct } from "../../../../modules/perf_reports/api/product/v4ProductReportByProduct";
import { useProductReportColumns } from "../../../../modules/perf_reports/product_report/useProductReportColumns";
import { trellisPalette } from "../../../custom/analytics/palettes";
import { DataViewPicker } from "../../../../modules/dataViews/components/DataViewPicker";

type ProductSelectorProps = {
  selectedRowIds?: string[] | number[];
  defaultSelectedColumns?: string[];
  enableMultiRowSelection?: boolean;
  enableSelectAll?: boolean;
  enableRowSelection?: boolean | (() => boolean);
  canSetPageSize?: boolean;
  hideClearAll?: boolean;
  hideSelectedFilter?: boolean;
  titleColWidth?: number;
  context: string;
  additionalFilters?: {
    categoryId?: string;
    productIds?: Array<string | number> | null;
    primaryVariant?: boolean;
    asins?: string[];
  };
  rowIdAccessor?: "product_id" | "product_key";
  onSelectionChange: (selected: string[]) => void;
};

export const ProductSelector = ({
  selectedRowIds = [],
  onSelectionChange,
  defaultSelectedColumns = ["product_title", "sales", "ad_sales", "ad_spend", "acos"],
  enableMultiRowSelection = true,
  enableSelectAll = true,
  enableRowSelection = true,
  canSetPageSize = true,
  context,
  additionalFilters = {},
  hideClearAll = false,
  hideSelectedFilter = false,
  rowIdAccessor = "product_id",
}: ProductSelectorProps) => {
  const { t } = useTranslation(["common", "merchandising", "advertising"]);
  const [viewSelected, setViewSelected] = useState(false);
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const columns = useProductReportColumns("productSelector");

  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id!),
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `productSelectorWidget-${context}`,
    initialState: {
      columnVisibility: defaultHiddenCols,
      pagination: {
        pageSize: 5,
        pageIndex: 0,
      },
      columnOrder: defaultSelectedColumns,
      columnPinning: {
        start: ["product_title"],
        end: [],
      },
      sorting: [{ id: "sales", desc: true }],
      rowSelection: selectedIdsToSelectionState(selectedRowIds),
    },
  });

  const filters = useMemo(() => {
    // Behaviour below allows multi-category product selection by default if filters on table level chosen, which overwrite the defaults
    const categoryFilter =
      (
        additionalFilters.categoryId &&
        !viewSelected &&
        !state.columnFilters.some((filter) => filter.id === "category_id")
      ) ?
        [{ id: "category_id", value: { op: "=", value: additionalFilters.categoryId } }]
      : [];
    const productIdsFilter =
      viewSelected ? [{ id: rowIdAccessor, value: { op: "anyof", value: selectedRowIds } }]
      : additionalFilters.productIds ?
        [{ id: rowIdAccessor, value: { op: "anyof", value: additionalFilters.productIds } }]
      : [];
    const primaryVariantFilter =
      additionalFilters.primaryVariant && !viewSelected ?
        [{ id: "primary_variant", value: { op: "=", value: true } }]
      : [];
    const asinsFilter =
      additionalFilters.asins?.length ? [{ id: "asin", value: { op: "anyof", value: additionalFilters.asins } }] : [];
    return [...state.columnFilters, ...categoryFilter, ...productIdsFilter, ...primaryVariantFilter, ...asinsFilter];
  }, [
    additionalFilters.categoryId,
    additionalFilters.productIds,
    additionalFilters.primaryVariant,
    additionalFilters.asins,
    viewSelected,
    state.columnFilters,
    rowIdAccessor,
    selectedRowIds,
  ]);

  const debounceSearch = useDebounce(state.globalFilter, 500);

  const hasEmptyIdRestriction = !viewSelected && additionalFilters.productIds?.length === 0;

  const byProductOptionsGetter = useGetV4ProductReportByProduct();
  const productByProductReportQuery = useBlockWindowQuery(
    byProductOptionsGetter,
    {
      pagination: state.pagination,
      sorting: state.sorting,
      filters: filters,
      search: debounceSearch,
    },
    { enabled: !hasEmptyIdRestriction },
  );

  const categoriesQuery = useCategoriesQuery();
  const productReportOptionsQuery = useQuery(useGetV4ProductReportOptions());

  const variantOptions = useMemo(() => {
    const { variations } = productReportOptionsQuery.data?.data ?? {};

    if (!variations) {
      return [];
    }

    return [
      { value: "all", label: t(($) => $.common["All Variants"]) },
      ...Object.keys(variations).flatMap((variationCategory) => {
        return (
          variations[variationCategory]?.map((variation) => ({
            label: `${toTitleCase(variationCategory)}: ${variation}`,
            value: { [variationCategory]: variation },
          })) ?? []
        );
      }),
    ];
  }, [productReportOptionsQuery.data?.data, t]);

  const tagOptions: { value: string; label: string }[] = useMemo((): { value: string; label: string }[] => {
    const tags = productReportOptionsQuery.data?.data.tags ?? [];

    return [
      { value: "all", label: t(($) => $.common["All Tags"]) },
      ...tags.map((tag: string): { value: string; label: string } => ({
        value: tag,
        label: tag,
      })),
    ];
  }, [productReportOptionsQuery.data?.data.tags, t]);

  const tagEnhancedOptions = useTagFilterOptions("PRODUCT", { enabled: tagsEnhancedEnabled });

  const categoryOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.common["All Categories"]) },
      ...(categoriesQuery.data?.map((category) => {
        return {
          value: category.id.toString(),
          label: category.leaf_category_name,
        };
      }) ?? []),
    ],
    [categoriesQuery.data, t],
  );

  const table = useServerTable({
    data: hasEmptyIdRestriction ? undefined : productByProductReportQuery.data?.data,
    columns,
    getRowCanExpand: () => true,
    getRowId: (row) => row[rowIdAccessor]!.toString(),
    enableMultiRowSelection: enableMultiRowSelection,
    enableRowSelection: enableRowSelection,
    rowCount: hasEmptyIdRestriction ? 0 : productByProductReportQuery.data?.count || 0,
    initialState: initialState,
    state: {
      ...state,
      rowSelection: selectedIdsToSelectionState(selectedRowIds),
    },
    ...setters,
    alternateSortingEnabled: true,
    onRowSelectionChange: (updater) => {
      const rowSelectionState = typeof updater === "function" ? updater(table.state.rowSelection) : updater;
      const updatedIds = Object.keys(rowSelectionState);
      onSelectionChange(updatedIds);
    },

    meta: {
      showSkeleton:
        !hasEmptyIdRestriction &&
        productByProductReportQuery.isFetching &&
        (!productByProductReportQuery.data || productByProductReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        category_id: categoryOptions,
        tags: tagOptions,
        "tags_enhanced.id": tagEnhancedOptions,
        variations: variantOptions.filter(Boolean),
        has_ads: [
          {
            value: true,
            label: t(($) => $.advertising["Active Ads"]),
          },
          {
            value: false,
            label: t(($) => $.advertising["No Active Ads"]),
          },
        ],
        inventory_status: [
          {
            value: "all",
            label: t(($) => $.merchandising["All Inventory States"]),
            description: t(($) => $.merchandising["Show products with any inventory state"]),
          },
          ...INVENTORY_STATE_OPTIONS.filter(Boolean),
        ],
        pricing_plan_status: ENROLL_OPTIONS,
        primary_variant: [
          {
            value: true,
            label: t(($) => $.common.Yes),
          },
          {
            value: false,
            label: t(($) => $.common.No),
          },
        ],
        sales_state: [
          {
            value: "all",
            label: t(($) => $.merchandising["All Sales States"]),
          },
          ...SALE_STATE_OPTIONS.filter((option) => option !== undefined),
        ],
      },
    },
  });

  return (
    <div className={css({ width: "100%" })}>
      <HStack justifyContent={"space-between"} padding={"small"} fontSize={"large"}>
        <Flex>
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "auto 1rem",
            })}
          >
            <span
              className={css({
                margin: "auto 1rem auto 0",
                fontSize: "1.5rem",
                fontWeight: 500,
              })}
            >
              {t(($) => $.common["Products Selected"], { count: selectedRowIds.length })}
            </span>
            {selectedRowIds.length > 0 && !hideSelectedFilter && (
              <Flex gap={"medium"}>
                <span
                  onClick={() => {
                    if (!viewSelected) {
                      table.resetColumnFilters();
                    }
                    setViewSelected(!viewSelected);
                  }}
                  className={css({
                    cursor: "pointer",
                    color: trellisPalette[0],
                    fontSize: "1.2rem",
                    fontWeight: 600,
                  })}
                >
                  {viewSelected ? t(($) => $.common["View All Options"]) : t(($) => $.common["View Selected"])}
                </span>
                {!hideClearAll && (
                  <span
                    onClick={() => onSelectionChange([])}
                    className={css({
                      cursor: "pointer",
                      color: trellisPalette[0],
                      fontSize: "1.2rem",
                      fontWeight: 600,
                    })}
                  >
                    {t(($) => $.common["Clear All"])}
                  </span>
                )}
              </Flex>
            )}
          </div>
        </Flex>
        <VStack>
          <HStack>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table, `productSelectorWidget-${context}`);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <DataViewPicker viewKey={`productSelectorWidget-${context}`} datasetKey="product" table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions.Search)}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
        </VStack>
        <RT8Pagination table={table} canSetPageSize={canSetPageSize} />
      </HStack>
      <RT8FilterWidget table={table} />
      <Table table={table} enableSelectAll={enableSelectAll} />
    </div>
  );
};
